/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  FormControl,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import {
  ApiError,
  GarantiasVehicularInmobiliariaResponse,
  PayloadRequestGarantias,
  PayloadRequestGarantiasVehicularInmobiliaria,
} from "@interfaces/index";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { usePatchGuaranteesTypesMutation, usePostGuaranteesTypesMutation } from "@api/garantiasApi";
import { GuaranteeFormHandle } from "./guaranteeFormHandle";

interface SolidariaProps {
  monto_patrimonial: string;
  id?: string;
}

interface FormGarantiaSolidariaProps {
  guaranteesData: GarantiasVehicularInmobiliariaResponse[];
  guaranteeTypeId?: string;
  hideSubmitButton?: boolean;
  guaranteeLabel?: string;
  multiFlow?: boolean;
  onStatusChange?: (status: { isValid: boolean; isSubmitting: boolean }) => void;
  loading?: boolean;
}

const FormGarantiaSolidaria = forwardRef<GuaranteeFormHandle, FormGarantiaSolidariaProps>(
  (
    {
      guaranteesData,
      guaranteeTypeId,
      hideSubmitButton,
      guaranteeLabel,
      onStatusChange,
      loading = false,
    }: FormGarantiaSolidariaProps,
    ref,
  ) => {
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useAppDispatch();
    const application = useSelector((state: RootState) => state.register.application);
    const [postGuarantees] = usePostGuaranteesTypesMutation();
    const [patchGuarantees] = usePatchGuaranteesTypesMutation();

    const emptySolidaria: SolidariaProps = {
      monto_patrimonial: "",
      id: undefined,
    };

    const [isLoadingState, setIsLoading] = useState(false);
    // se bloquea tanto por su propia carga como cuando el padre está guardando todas las garantías
    const isLoading = isLoadingState || loading;

    const lastSubmitOkRef = useRef(false);

    type FormValues = {
      solidarias: SolidariaProps[];
    };

    const formik = useFormik<FormValues>({
      initialValues: {
        solidarias: [{ ...emptySolidaria }],
      },
      validateOnMount: true,
      validationSchema: yup.object().shape({
        solidarias: yup.array().of(
          yup.object().shape({
            monto_patrimonial: yup
              .string()
              .required("Campo requerido")
              .test("monto-valido", "Ingresa un monto mayor a 0", (value) => {
                if (!value) return true;
                return Number(value.replace(/,/g, "")) > 0;
              }),
          }),
        ),
      }),
      onSubmit: async (values, { setSubmitting }) => {
        try {
          lastSubmitOkRef.current = false;
          setIsLoading(true);
          setSubmitting(true);

          const guaranteesToPatch: Array<{ guarantee_id: string; data: PayloadRequestGarantiasVehicularInmobiliaria }> =
            [];
          const guaranteesToPost: PayloadRequestGarantiasVehicularInmobiliaria[] = [];

          values.solidarias.forEach((solidaria) => {
            const cleanValue = Number(solidaria.monto_patrimonial.replace(/,/g, "")) || 0;

            const guaranteeItem: PayloadRequestGarantiasVehicularInmobiliaria = {
              loan_application_id: application.id,
              ...(guaranteeTypeId ? { guarantee_type_id: guaranteeTypeId } : {}),
              estimated_value: cleanValue,
            };

            if (solidaria?.id) {
              guaranteesToPatch.push({ guarantee_id: solidaria.id, data: guaranteeItem });
            } else {
              guaranteesToPost.push(guaranteeItem);
            }
          });

          // Si existe id => PATCH (uno por garantía). Si no existe => POST (en lista)
          await Promise.all([
            ...guaranteesToPatch.map(({ guarantee_id, data }) =>
              patchGuarantees({ loan_application_id: application.id, guarantee_id, data }).unwrap(),
            ),
            guaranteesToPost.length > 0
              ? postGuarantees({
                  loan_application_id: application.id,
                  data: { guarantees_list: guaranteesToPost } as PayloadRequestGarantias,
                }).unwrap()
              : Promise.resolve(),
          ]);

          lastSubmitOkRef.current = true;
        } catch (error) {
          lastSubmitOkRef.current = false;
          const api_error = error as ApiError;

          Sentry.captureException(error, {
            extra: {
              component: "InfoGarantiaSolidaria",
              values: formik.values,
            },
          });

          dispatch(setModalErrorVisible({ open: true, error: api_error }));
        } finally {
          setSubmitting(false);
          setIsLoading(false);
        }
      },
    });

    useEffect(() => {
      onStatusChange?.({ isValid: formik.isValid, isSubmitting: formik.isSubmitting });
    }, [formik.isValid, formik.isSubmitting, onStatusChange]);

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          lastSubmitOkRef.current = false;

          const errors = await formik.validateForm();
          if (Object.keys(errors).length > 0) {
            formik.setTouched({
              solidarias: formik.values.solidarias.map(() => ({
                monto_patrimonial: true,
              })),
            });
            return false;
          }

          await formik.submitForm();
          return lastSubmitOkRef.current;
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [formik.values.solidarias],
    );

    useEffect(() => {
      const mappedSolidarias = guaranteesData
        .filter((item) => item.guarantee_type_id === guaranteeTypeId && item.active)
        .map((item) => {
          const num = Number(item.estimated_value);

          return {
            monto_patrimonial: num > 0 ? num.toLocaleString("en-US") : "",
            id: item.id,
          };
        });

      if (mappedSolidarias.length > 0) {
        formik.setFieldValue("solidarias", mappedSolidarias);
      }
    }, [guaranteesData]);

    const getFieldError = (fieldName: keyof SolidariaProps, index: number): string | undefined => {
      const solidariaErrors = formik.errors.solidarias as
        Array<Partial<Record<keyof SolidariaProps, string>>> | undefined;
      if (solidariaErrors && solidariaErrors[index] && solidariaErrors[index][fieldName]) {
        return solidariaErrors[index][fieldName];
      }
      return undefined;
    };

    const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const rawValue = e.target.value.replace(/[^0-9]/g, "");
      const formattedValue = rawValue ? Number(rawValue).toLocaleString("en-US") : "";
      formik.setFieldValue(`solidarias[${index}].monto_patrimonial`, formattedValue);
    };

    return (
      <Box>
        <form onSubmit={formik.handleSubmit} noValidate>
          <Typography
            mt={isMobileOrTablet ? 2 : 3}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color="primary.dark"
            textAlign={"center"}
          >
            Detalle de tu garantía {guaranteeLabel}
          </Typography>

          {formik.values.solidarias.map((solidaria: SolidariaProps, index: number) => (
            <Box key={index} mt={index > 0 ? 4 : 0}>
              <FormControl fullWidth>
                <TextField
                  id={`monto_patrimonial-${index}`}
                  name={`solidarias[${index}].monto_patrimonial`}
                  label="Monto de relación patrimonial (estimado)"
                  margin="normal"
                  fullWidth
                  required
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeValue(e, index)}
                  inputProps={{ maxLength: 20, inputMode: "numeric" }}
                  value={solidaria.monto_patrimonial}
                  disabled={formik.isSubmitting || isLoading}
                  error={Boolean(
                    formik.touched.solidarias?.[index]?.monto_patrimonial && getFieldError("monto_patrimonial", index),
                  )}
                  helperText={
                    formik.touched.solidarias?.[index]?.monto_patrimonial
                      ? getFieldError("monto_patrimonial", index)
                      : ""
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </FormControl>
            </Box>
          ))}

          {!hideSubmitButton && (
            <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
              <Button
                variant="default"
                type="submit"
                color="primary"
                disabled={formik.isSubmitting || isLoading || !formik.isValid}
              >
                Continuar
              </Button>
            </Box>
          )}
        </form>
      </Box>
    );
  },
);

FormGarantiaSolidaria.displayName = "FormGarantiaSolidaria";

export default FormGarantiaSolidaria;
